import productLauncher from './launcher/product.launcher';
import productWeb from './web/product.web';
import productMock from './mock/product.mock';
import {resolveBridgeType, BridgeType, ResponseType} from './BridgeApi';

// Const for special case in refetchUserBuilds that re-fetches every namespace
export const REFETCH_ALL_USER_BUILDS_KEY = '';

export interface ProductInstallState {
    appname: string;
    catalogid: string;
    hasoptionalinstalls: boolean;
    hasupdate: boolean;
    installalerttext?: string;
    installalertverbosity: string;
    installedversion?: string;
    installsizeondisk: number;
    isbanned: boolean;
    iscloudsyncing?: boolean;
    iscorrupt: boolean;
    isinitialized: boolean;
    isinstalled: boolean;
    isinupdatequeue: boolean;
    ispartiallyinstalled: boolean;
    isreadytoinstall: boolean;
    isrunning: boolean;
    isupdating: boolean;
    iswaitinginline: boolean;
    lastcloudsync?: string;
    namespace: string;
    neverupdate: boolean;
    progress: number;
    retrytime?: string;
    serverstatus?: string;
    serverstatustext?: string;
    supportscloudsave?: boolean;
    updatestatus: string;
    waittime?: string;
}

export interface DLCState {
    appname: string;
    bshouldbeinstalled: boolean;
    catalogid: string;
    namespace: string;
}

export type ProductResponseCallback = (
    productState: ProductInstallState,
    responseType: ResponseType
) => void;

export interface ProductBridge {
    getInstalledApps(): Promise<ProductInstallState[]>;
    getApp(namespace: string, catalogId: string, appName: string): Promise<ProductInstallState>;
    install(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    uninstall(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    update(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    pauseInstallation(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    resumeInstallation(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    cancelInstallation(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    launch(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string,
        options?: any
    ): Promise<boolean>;
    cancelWaitingRoom(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    showInstallerOptions(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    verifyInstallation(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    createShortcut(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    getRecentlyPlayed(maxResults: number): Promise<ProductInstallState[]>;
    getGlobalAutoUpdate(): Promise<boolean>;
    setAutoUpdate(
        namespace: string,
        catalogId: string,
        appName: string,
        autoUpdate: boolean,
        funnelId: string
    ): Promise<boolean>;
    getAutoUpdate(namespace: string, catalogId: string, appName: string): Promise<boolean>;

    setCloudSave(
        namespace: string,
        catalogId: string,
        appName: string,
        enableSaveSync: boolean,
        funnelId: string
    ): Promise<boolean>;

    getCloudSave(namespace: string, catalogId: string, appName: string): Promise<boolean>;

    syncCloudSave(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;

    registerResponseCallback(callback: ProductResponseCallback): Promise<void>;
    goToInstallAlert(
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean>;
    refetchUserBuilds(): Promise<boolean>;
    setAppConfiguration: (
        namespace: string,
        catalogId: string,
        appName: string,
        dlcState: DLCState[]
    ) => Promise<boolean>;
    getAppConfiguration: (
        namespace: string,
        catalogId: string,
        appName: string
    ) => Promise<DLCState[]>;
}

const bridges = {
    [BridgeType.LAUNCHER]: productLauncher,
    [BridgeType.WEB]: productWeb,
    [BridgeType.MOCK]: productMock
};

export default (): ProductBridge => bridges[resolveBridgeType()];
